import { useAuth } from '@clerk/clerk-react';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';

import { ErrorScreen } from './components/error-screen';
import { LoadingScreen } from './components/loading-screen';
import { trackError } from './error/tracking';
import './index.css';
import { captureException } from './logger/capture-exception';
import { routeTree } from './routeTree.gen';
import { AppRouterAuth } from './router-auth';
import { AppRouterUnauth } from './router-unauth';
import { setSocket } from './sdk/socket';
import { getStores } from './services/storage';
import { useToast } from './shared/contexts/toast-context';

export const router = createRouter({
  routeTree,
  defaultErrorComponent: ErrorScreen,
  defaultPendingComponent: LoadingScreen,
});
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function handleRetrieveToken(raw?: () => Promise<string | null | undefined>) {
  return async function (bypassRaw?: boolean) {
    let token = '';
    if (!bypassRaw && raw != null) {
      token = (await raw()) ?? '';
    }

    // already have something
    if (token.length > 0) {
      return token;
    }

    // get from the clerk utility library
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const clerkGetToken: () => Promise<string> = (window as any).Clerk?.session?.getToken;
    if (typeof clerkGetToken === 'function') {
      token = (await clerkGetToken()) ?? token;
    }

    // already have something
    if (token.length > 0) {
      return token;
    }

    return token;
  };
}

export function AppRouter() {
  const { signOut, getToken } = useAuth();
  const addToast = useToast();

  const location = window.location.href;
  const isRaw = location.indexOf('oauthcb') !== -1 || location.indexOf('auth/wait-user') !== -1;
  const isNoAuthPage = location.indexOf('auth') !== -1 && !isRaw;

  // update the getters on authorization with the incoming signOut
  const tokenGetter = useCallback(
    function (bypassRaw?: boolean) {
      return handleRetrieveToken(getToken)(bypassRaw);
    },
    [getToken]
  );
  getStores().users(function (state) {
    state.setAuthOnStore(tokenGetter, signOut);
  });

  // setup the socket with the token incoming
  useEffect(
    function () {
      setSocket(tokenGetter).catch(function (err) {
        captureException(
          err,
          {
            file: 'router.tsx',
            method: 'setSocket',
            infoMessage: 'unable to setups socket',
          },
          trackError,
          addToast
        );
      });
    },
    [getToken, tokenGetter, addToast]
  );

  // this provider is for to be used when we want a page to not any of the middlewares
  // instigated by other routes like being or not being auth
  // it is to be used when we want a black pange with full control on the component
  if (isRaw) {
    return <RouterProvider router={router} notFoundMode="root" />;
  }

  if (isNoAuthPage) {
    return <AppRouterUnauth router={router} />;
  }

  return <AppRouterAuth router={router} />;
}
